import React, { useState } from "react";
import ReactDOM from "react-dom";
import Swal from 'sweetalert2';
import { FaEye, FaEyeSlash } from 'react-icons/fa'; 
import { FiUser, FiMail, FiLock, FiShield, FiX, FiUserPlus } from 'react-icons/fi';
import { useSelector } from "react-redux";
import apiClient from '../../../api/apiClient';

function AddUserModal({ isOpen, onClose, refreshUsers }) {
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const [first_name, setFirst_name] = useState("");
  const [last_name, setLast_name] = useState(""); 
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useSelector((state) => state.auth);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await apiClient.post(`/api/users/create-user/`, {
        email, password, role, first_name, last_name 
      });
      Swal.fire({
        icon: 'success',
        title: 'User Added Successfully!',
        text: `Welcome ${first_name} ${last_name} to the team!`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        background: 'linear-gradient(135deg, #10b981, #059669)',
        color: 'white'
      });
      setPassword("");
      setEmail("");
      setRole("admin");
      setFirst_name("");
      setLast_name("");
      refreshUsers();
      onClose(); 
    } catch (error) {
      const errorResponse = error.response?.data;
      let errorMessage = 'An error occurred.';

      if (errorResponse) {
        const errors = Object.values(errorResponse).flat();
        errorMessage = errors.join(' ');
      }

      Swal.fire({
        icon: 'error',
        title: 'Oops! Something went wrong',
        text: errorMessage,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        background: 'linear-gradient(135deg, #ef4444, #dc2626)',
        color: 'white'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleColor = (roleValue) => {
    const colors = {
      admin: "from-green-500 to-teal-500",
      reviewer: "from-blue-500 to-cyan-500",
      tasker: "from-orange-500 to-amber-500"
    };
    return colors[roleValue] || "from-gray-500 to-gray-600";
  };


  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50">
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 dark:border-gray-700/30 max-w-2xl w-full mx-4 overflow-hidden">
        {/* Header with Gradient */}
        <div className="relative bg-gradient-to-r from-green-500 to-teal-500 p-8 text-white">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <FiUserPlus className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Add New User</h2>
                <p className="text-green-100 mt-1">Create a new team member account</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {/* Name Fields Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="group">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                <FiUser className="inline w-4 h-4 mr-2 text-green-500" />
                First Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={first_name}
                  onChange={(e) => setFirst_name(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all group-hover:border-green-300"
                  placeholder="Enter first name"
                  required
                />
              </div>
            </div>
            
            <div className="group">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                <FiUser className="inline w-4 h-4 mr-2 text-green-500" />
                Last Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={last_name}
                  onChange={(e) => setLast_name(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all group-hover:border-green-300"
                  placeholder="Enter last name"
                  required
                />
              </div>
            </div>
          </div>

          {/* Email Field */}
          <div className="group">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              <FiMail className="inline w-4 h-4 mr-2 text-green-500" />
              Email Address
            </label>
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all group-hover:border-green-300"
                placeholder="Enter email address"
                required
              />
            </div>
          </div>

          {/* Password Field */}
          <div className="group">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              <FiLock className="inline w-4 h-4 mr-2 text-green-500" />
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all group-hover:border-green-300"
                placeholder="Enter secure password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-green-500 transition-colors p-1"
              >
                {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Role Selection with Visual Cards */}
          <div className="group">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
              <FiShield className="inline w-4 h-4 mr-2 text-green-500" />
              Select Role
            </label>
            <div className="grid grid-cols-3 gap-3">
              {["admin", "reviewer", "tasker"].map((roleOption) => (
                <label key={roleOption} className="cursor-pointer">
                  <input
                    type="radio"
                    name="role"
                    value={roleOption}
                    checked={role === roleOption}
                    onChange={(e) => setRole(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`p-4 rounded-xl border-2 transition-all text-center ${
                    role === roleOption
                      ? `bg-gradient-to-r ${getRoleColor(roleOption)} text-white border-transparent shadow-lg scale-105`
                      : "border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/50 hover:border-green-300 hover:bg-green-50/50 dark:hover:bg-green-900/20"
                  }`}>
                    <div className={`font-semibold capitalize text-sm ${
                      role === roleOption ? "text-white" : "text-gray-700 dark:text-gray-300"
                    }`}>
                      {roleOption}
                    </div>
                    <div className={`text-xs mt-1 ${
                      role === roleOption ? "text-green-100" : "text-gray-500 dark:text-gray-400"
                    }`}>
                      {roleOption === "admin" ? "Full access" : 
                       roleOption === "reviewer" ? "Review & approve" : "Task execution"}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl transition-all font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white rounded-xl transition-all font-medium shadow-lg hover:shadow-xl ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Creating...
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <FiUserPlus className="w-4 h-4" />
                  Add User
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default AddUserModal;